import React, { FC, useContext } from "react";
import { GridLegacy as Grid, useMediaQuery, useTheme } from "@mui/material";

import { IBookingRecordOptions, IContactRecordOptions, IPreferences, LuggageType } from "../../logic/api";
import { isSysAdmin } from "../../utility/utils";

import EditableField, { EditableFieldTypes } from "../../components/editable/EditableField/EditableField";
import EditableGroup, { EditableGroupType } from "../../components/editable/EditableGroup/EditableGroup";
import { IDropDownListItem } from "../../components/editable/EditableField/fields/DropDownField";
import NotchedCard from "../../components/cards/NotchedCard/NotchedCard";

import { SessionContext } from "../../utility/useSession";
import styles from "./styles";
import { useTranslation } from "react-i18next";
import { useReleaseManagement, ReleaseFlag } from "../../utility/feature-management";


interface IContactRecordSettingsProps {
  onSave: (data: Partial<IPreferences>) => Promise<boolean>;
}

const LABEL_COLUMN = { sm: 4, xs: 12 } as const;
const EXTRA_COLUMN = { sm: 4, xs: 12 } as const;
const TOGGLE_COLUMN = { sm: 2, xs: 6 } as const;

const EXTRA_COLUMN_SX = { display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" } } as const;

const DROPDOWN_MAX_WIDTH = 260;

const FIRST_NAME_INITIAL_OPTIONS = {
  fullName: "fullName",
  initial: "initial",
} as const;

const clampLuggageMinutes = (value: number): number => Math.min(99, Math.max(0, Math.round(value)));

const getLuggageMinutes = (data: IPreferences, type: LuggageType): number =>
  data.luggageMinutesToCarPark?.find((item) => item.type === type)?.minutes ?? 0;

const setLuggageMinutes = (data: IPreferences, type: LuggageType, minutes: number): void => {
  const values = [...(data.luggageMinutesToCarPark ?? [])];
  const index = values.findIndex((item) => item.type === type);

  if (index === -1) {
    values.push({ type, minutes: clampLuggageMinutes(minutes) });
  } else {
    values[index] = { ...values[index], minutes: clampLuggageMinutes(minutes) };
  }

  data.luggageMinutesToCarPark = values;
};

export const ContactRecordSettings: FC<IContactRecordSettingsProps> = ({ onSave }) => {
  const session = useContext(SessionContext);
  const theme = useTheme();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down("xs"));
  const { t } = useTranslation();
  const isAdditionalCustomizableFieldsReleased = useReleaseManagement(ReleaseFlag.releaseAdditionalCustomizableFields);
  const isImproveContactRecordOptionsReleased = useReleaseManagement(ReleaseFlag.releaseImproveContactRecordOptions);
  const isAddLuggageFieldToBookingReleased = useReleaseManagement(ReleaseFlag.releaseAddLuggageFieldToBooking);

  if (!session.current.site) {
    return null;
  }

  if (!isSysAdmin(session)) {
    return null;
  }

  const labelStyle = isSmallDevice
    ? { textAlign: "center" as const }
    : { paddingTop: 40 };

  const ensureContactRecordOptions = (data: IPreferences): IContactRecordOptions => {
    if (!data.contactRecordOptions) {
      data.contactRecordOptions = { properties: {} };
    }

    return data.contactRecordOptions;
  };

  const ensureBookingRecordOptions = (data: IPreferences): IBookingRecordOptions => {
    if (!data.bookingRecordOptions) {
      data.bookingRecordOptions = { properties: {} };
    }

    return data.bookingRecordOptions;
  };

  const emptyField = (
    <EditableField<IPreferences, string>
      inputType={EditableFieldTypes.empty}
      label={""}
    />
  );

  const firstNameInitialOptions = (): IDropDownListItem[] => [
    { name: t("booking.labels.firstName"), value: FIRST_NAME_INITIAL_OPTIONS.fullName },
    { name: t("booking.labels.initial"), value: FIRST_NAME_INITIAL_OPTIONS.initial },
  ];

  const firstNameInitialSelection = (data: IPreferences): IDropDownListItem => {
    const options = firstNameInitialOptions();
    return data.booking_leadContact_firstName_enabled
      ? options[0]
      : options[1];
  };

  return (
    <Grid item xs={12}>
      <NotchedCard label={t(isImproveContactRecordOptionsReleased ? "settings.customerRecOptions" : "settings.contactRecOptions")} style={styles.card}>
        <EditableGroup<IPreferences>
          id="contactRecordOptions"
          editType={EditableGroupType.switchableEditing}
          data={session.current.site.preferences}
          onDataChanged={(data) => onSave({
            contactRecordOptions: data.contactRecordOptions,
            ...(isImproveContactRecordOptionsReleased && {
              booking_leadContact_title_enabled: data.booking_leadContact_title_enabled,
              booking_leadContact_title_dropdown: data.booking_leadContact_title_dropdown,
              booking_leadContact_firstName_enabled: data.booking_leadContact_firstName_enabled,
            }),
            ...(isAddLuggageFieldToBookingReleased && {
              bookingRecordOptions: data.bookingRecordOptions,
              luggageMinutesToCarPark: data.luggageMinutesToCarPark,
            }),
          })}
          testId="contact-record-options"
        >
          {isImproveContactRecordOptionsReleased && (
            <>
              {/* Title */}
              <Grid container>
                <Grid item {...LABEL_COLUMN}>
                  <div style={labelStyle}><strong>{t("booking.labels.title")}</strong></div>
                </Grid>
                <Grid item {...EXTRA_COLUMN} sx={EXTRA_COLUMN_SX}>
                  <EditableField<IPreferences, boolean>
                    useFullWidthWhenEditing={false}
                    testId="settings-title-dropdown-switch"
                    inputType={EditableFieldTypes.switch}
                    label={t("settings.dropdown")}
                    onProvide={(data) => data.booking_leadContact_title_dropdown}
                    onChange={(data, value) => {
                      data.booking_leadContact_title_dropdown = value;

                      if (value) {
                        data.booking_leadContact_title_enabled = true;
                      }
                    }}
                  />
                </Grid>
                <Grid item {...TOGGLE_COLUMN}>
                  <EditableField<IPreferences, boolean>
                    useFullWidthWhenEditing={false}
                    inputType={EditableFieldTypes.switch}
                    label={t("settings.visible")}
                    onProvide={(data) => Boolean(data.booking_leadContact_title_enabled)}
                    onChange={(data, value) => {
                      data.booking_leadContact_title_enabled = value;

                      if (!value) {
                        const existing = data.contactRecordOptions?.properties.title;
                        ensureContactRecordOptions(data).properties.title = {
                          useDropdown: existing?.useDropdown ?? Boolean(data.booking_leadContact_title_dropdown),
                          required: false,
                        };
                      }
                    }}
                    testId="settings-contact-record-title-visible-switch"
                  />
                </Grid>
                <Grid item {...TOGGLE_COLUMN}>
                  <EditableField<IPreferences, boolean>
                    useFullWidthWhenEditing={false}
                    inputType={EditableFieldTypes.switch}
                    label={t("settings.mandatory")}
                    onProvide={(data) => Boolean(data.contactRecordOptions?.properties.title?.required)}
                    onChange={(data, value) => {
                      const existing = data.contactRecordOptions?.properties.title;
                      ensureContactRecordOptions(data).properties.title = {
                        useDropdown: existing?.useDropdown ?? Boolean(data.booking_leadContact_title_dropdown),
                        required: Boolean(value),
                      };

                      if (value) {
                        data.booking_leadContact_title_enabled = true;
                      }
                    }}
                    testId="settings-contact-record-title-mandatory-switch"
                  />
                </Grid>
              </Grid>

              {/* First Name / Initial */}
              <Grid container>
                <Grid item {...LABEL_COLUMN}>
                  <div style={labelStyle}><strong>{t("settings.firstNameOrInitial")}</strong></div>
                </Grid>
                <Grid item {...EXTRA_COLUMN} sx={EXTRA_COLUMN_SX}>
                  <EditableField<IPreferences, IDropDownListItem>
                    useFullWidthWhenEditing={false}
                    maxWidth={DROPDOWN_MAX_WIDTH}
                    testId="settings-contact-record-first-name-initial-dropdown"
                    inputType={EditableFieldTypes.enumDropDownField}
                    label={t("settings.firstNameOrInitial")}
                    onProvide={(data) => firstNameInitialSelection(data)}
                    onRequestText={(data) => firstNameInitialSelection(data).name}
                    onRequestData={() => Promise.resolve(firstNameInitialOptions())}
                    onChange={(data, value) => {
                      data.booking_leadContact_firstName_enabled = value.value === FIRST_NAME_INITIAL_OPTIONS.fullName;
                    }}
                  />
                </Grid>
                <Grid item {...TOGGLE_COLUMN}>
                  <EditableField<IPreferences, boolean>
                    useFullWidthWhenEditing={false}
                    inputType={EditableFieldTypes.switch}
                    label={t("settings.visible")}
                    onProvide={(data) => data.contactRecordOptions?.properties.firstName?.visible ?? true}
                    onChange={(data, value) => {
                      const existing = data.contactRecordOptions?.properties.firstName;
                      ensureContactRecordOptions(data).properties.firstName = {
                        required: value ? (existing?.required ?? false) : false,
                        useInitial: existing?.useInitial ?? !data.booking_leadContact_firstName_enabled,
                        visible: Boolean(value),
                      };
                    }}
                    testId="settings-contact-record-first-name-visible-switch"
                  />
                </Grid>
                <Grid item {...TOGGLE_COLUMN}>
                  <EditableField<IPreferences, boolean>
                    useFullWidthWhenEditing={false}
                    inputType={EditableFieldTypes.switch}
                    label={t("settings.mandatory")}
                    onProvide={(data) => Boolean(data.contactRecordOptions?.properties.firstName?.required)}
                    onChange={(data, value) => {
                      const existing = data.contactRecordOptions?.properties.firstName;
                      ensureContactRecordOptions(data).properties.firstName = {
                        required: Boolean(value),
                        useInitial: existing?.useInitial ?? !data.booking_leadContact_firstName_enabled,
                        visible: value ? true : (existing?.visible ?? true),
                      };
                    }}
                    testId="settings-contact-record-first-name-mandatory-switch"
                  />
                </Grid>
              </Grid>
            </>
          )}

          {/* Passengers */}
          <Grid container>
            <Grid item {...LABEL_COLUMN}>
              <div style={labelStyle}><strong>{t("booking.labels.passengers")}</strong></div>
            </Grid>
            <Grid item {...EXTRA_COLUMN} sx={EXTRA_COLUMN_SX}>
              {isImproveContactRecordOptionsReleased ? (
                <EditableField<IPreferences, number>
                  useFullWidthWhenEditing={false}
                  inputType={EditableFieldTypes.number}
                  label={t("settings.defaultPassengers")}
                  onProvide={(data) => data.contactRecordOptions?.properties.passengers?.default ?? 1}
                  onChange={(data, value) => {
                    const intValue = Math.round(value);

                    if (intValue > 0) {
                      const existing = data.contactRecordOptions?.properties.passengers;
                      ensureContactRecordOptions(data).properties.passengers = { ...(existing ?? { required: true }), default: intValue };
                    }
                  }}
                  testId="settings-contact-record-passengers-default-field"
                />
              ) : emptyField}
            </Grid>
            <Grid item {...TOGGLE_COLUMN}>
              <EditableField<IPreferences, boolean>
                useFullWidthWhenEditing={false}
                inputType={EditableFieldTypes.switch}
                label={t("settings.visible")}
                onProvide={(data) => Boolean(data.contactRecordOptions?.properties.passengers)}
                onChange={(data, value) => {
                  const existing = data.contactRecordOptions?.properties.passengers;
                  ensureContactRecordOptions(data).properties.passengers = value ? { ...existing, required: true } : undefined;
                }}
                testId="settings-contact-record-passengers-visible-switch"
              />
            </Grid>
            <Grid item {...TOGGLE_COLUMN}>
              <EditableField<IPreferences, boolean>
                useFullWidthWhenEditing={false}
                inputType={EditableFieldTypes.switch}
                label={t("settings.mandatory")}
                readOnly
                onProvide={(data) => Boolean(data.contactRecordOptions?.properties.passengers?.required)}
                onChange={(data, value) => {
                  const existing = data.contactRecordOptions?.properties.passengers;
                  ensureContactRecordOptions(data).properties.passengers = { ...existing, required: Boolean(value) };
                }}
                testId="settings-contact-record-passengers-mandatory-switch"
              />
            </Grid>
          </Grid>

          {isAdditionalCustomizableFieldsReleased && <>
            {/* Email */}
            <Grid container>
              <Grid item {...LABEL_COLUMN}>
                <div style={labelStyle}><strong>{t("booking.labels.email")}</strong></div>
              </Grid>
              <Grid item {...EXTRA_COLUMN} sx={EXTRA_COLUMN_SX}>
                {emptyField}
              </Grid>
              <Grid item {...TOGGLE_COLUMN}>
                <EditableField<IPreferences, boolean>
                  useFullWidthWhenEditing={false}
                  inputType={EditableFieldTypes.switch}
                  label={t("settings.visible")}
                  onProvide={(data) => Boolean(data.contactRecordOptions?.properties.email)}
                  onChange={(data, value) => ensureContactRecordOptions(data).properties.email = value ? { required: false } : undefined}
                  testId="settings-contact-record-email-visible-switch"
                />
              </Grid>
              <Grid item {...TOGGLE_COLUMN}>
                <EditableField<IPreferences, boolean>
                  useFullWidthWhenEditing={false}
                  inputType={EditableFieldTypes.switch}
                  label={t("settings.mandatory")}
                  onProvide={(data) => Boolean(data.contactRecordOptions?.properties.email?.required)}
                  onChange={(data, value) => ensureContactRecordOptions(data).properties.email = { required: Boolean(value) }}
                  testId="settings-contact-record-email-mandatory-switch"
                />
              </Grid>
            </Grid>

            {/* Mobile */}
            <Grid container>
              <Grid item {...LABEL_COLUMN}>
                <div style={labelStyle}><strong>{t("booking.labels.mobile")}</strong></div>
              </Grid>
              <Grid item {...EXTRA_COLUMN} sx={EXTRA_COLUMN_SX}>
                {emptyField}
              </Grid>
              <Grid item {...TOGGLE_COLUMN}>
                <EditableField<IPreferences, boolean>
                  useFullWidthWhenEditing={false}
                  inputType={EditableFieldTypes.switch}
                  label={t("settings.visible")}
                  onProvide={(data) => Boolean(data.contactRecordOptions?.properties.mobile)}
                  onChange={(data, value) => ensureContactRecordOptions(data).properties.mobile = value ? { required: false } : undefined}
                  testId="settings-contact-record-mobile-visible-switch"
                />
              </Grid>
              <Grid item {...TOGGLE_COLUMN}>
                <EditableField<IPreferences, boolean>
                  useFullWidthWhenEditing={false}
                  inputType={EditableFieldTypes.switch}
                  label={t("settings.mandatory")}
                  onProvide={(data) => Boolean(data.contactRecordOptions?.properties.mobile?.required)}
                  onChange={(data, value) => ensureContactRecordOptions(data).properties.mobile = { required: Boolean(value) }}
                  testId="settings-contact-record-mobile-mandatory-switch"
                />
              </Grid>
            </Grid>
          </>}

          {isAddLuggageFieldToBookingReleased && (
            <>
              <Grid container>
                <Grid item {...LABEL_COLUMN}>
                  <div style={labelStyle}><strong>{t("settings.luggage")}</strong></div>
                </Grid>
                <Grid item {...EXTRA_COLUMN} sx={EXTRA_COLUMN_SX}>
                  {emptyField}
                </Grid>
                <Grid item {...TOGGLE_COLUMN}>
                  <EditableField<IPreferences, boolean>
                    useFullWidthWhenEditing={false}
                    inputType={EditableFieldTypes.switch}
                    label={t("settings.visible")}
                    onProvide={(data) => Boolean(data.bookingRecordOptions?.properties.luggage)}
                    onChange={(data, value) => ensureBookingRecordOptions(data).properties.luggage = value ? { required: false } : null}
                    testId="settings-booking-record-luggage-visible-switch"
                  />
                </Grid>
                <Grid item {...TOGGLE_COLUMN}>
                  <EditableField<IPreferences, boolean>
                    useFullWidthWhenEditing={false}
                    inputType={EditableFieldTypes.switch}
                    label={t("settings.mandatory")}
                    onProvide={(data) => Boolean(data.bookingRecordOptions?.properties.luggage?.required)}
                    onChange={(data, value) => ensureBookingRecordOptions(data).properties.luggage = { required: Boolean(value) }}
                    testId="settings-booking-record-luggage-mandatory-switch"
                  />
                </Grid>
              </Grid>

              {/* Luggage time to car park */}
              <p style={{ marginTop: 40, marginBottom: 0 }}><strong>{t("settings.luggageTimeToCarPark.title")}</strong></p>
              <Grid container>
                <Grid item sm={6} xs={12}>
                  <EditableField<IPreferences, number>
                    testId="settings-luggage-hand-minutes-field"
                    inputType={EditableFieldTypes.number}
                    label={t("settings.luggageTimeToCarPark.handLuggage")}
                    onProvide={(data) => getLuggageMinutes(data, LuggageType.Hand)}
                    onChange={(data, value) => setLuggageMinutes(data, LuggageType.Hand, value)}
                  />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <EditableField<IPreferences, number>
                    testId="settings-luggage-hold-minutes-field"
                    inputType={EditableFieldTypes.number}
                    label={t("settings.luggageTimeToCarPark.holdLuggage")}
                    onProvide={(data) => getLuggageMinutes(data, LuggageType.Hold)}
                    onChange={(data, value) => setLuggageMinutes(data, LuggageType.Hold, value)}
                  />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <EditableField<IPreferences, number>
                    testId="settings-luggage-oversize-minutes-field"
                    inputType={EditableFieldTypes.number}
                    label={t("settings.luggageTimeToCarPark.oversizeLuggage")}
                    onProvide={(data) => getLuggageMinutes(data, LuggageType.Oversize)}
                    onChange={(data, value) => setLuggageMinutes(data, LuggageType.Oversize, value)}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </EditableGroup>
      </NotchedCard>
    </Grid>
  );
};
